import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Fingerprint, KeyRound, Lock, MonitorSmartphone, PenLine, ShieldCheck, Trash2, Users, Wifi } from "lucide-react";
import { toast } from "sonner";
import { Gate } from "@/components/wallet/Gate";
import { SiteFooter } from "@/components/wallet/SiteFooter";
import { useWallet } from "@/lib/wallet/store";
import { FEE_DESTINATION, feeSchedule } from "@/lib/wallet/fees";
import { MAINNET_ENDPOINTS } from "@/lib/wallet/xrpl";
import { addressFingerprint, truncateAddress } from "@/lib/wallet/format";

export const Route = createFileRoute("/settings")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Settings — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Review your key custody, auto-lock timer, large-transfer guard and network endpoints for your XRPL wallet.",
      },
      { property: "og:title", content: "Settings — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Keys stay on this device, encrypted. Inspect and tune every guard.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <SettingsScreen />
    </Gate>
  ),
});

const LOCK_OPTIONS = [
  { label: "30s", value: 30 },
  { label: "2 min", value: 120 },
  { label: "5 min", value: 300 },
  { label: "15 min", value: 900 },
];

function SettingsScreen() {
  const {
    vault,
    address,
    settings,
    updateSettings,
    lock,
    wipe,
    session,
    changePassphrase,
    biometricsEnabled,
    biometricsSupported,
    enableBiometrics,
    disableBiometrics,
  } = useWallet();
  const navigate = useNavigate();
  const [confirmWipe, setConfirmWipe] = useState("");
  const [currentPass, setCurrentPass] = useState("");
  const [nextPass, setNextPass] = useState("");
  const [repeatPass, setRepeatPass] = useState("");
  const [rotating, setRotating] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);

  const toggleBiometrics = async () => {
    setBioBusy(true);
    try {
      if (biometricsEnabled) {
        disableBiometrics();
        toast.success("Biometric unlock removed from this device.");
      } else {
        await enableBiometrics();
        toast.success("Biometric unlock enabled. Your passcode stays as backup.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update biometric unlock.");
    } finally {
      setBioBusy(false);
    }
  };

  const rotate = async () => {
    if (nextPass !== repeatPass) {
      toast.error("The new passcodes do not match.");
      return;
    }
    setRotating(true);
    try {
      await changePassphrase(vault?.kind === "passphrase" ? currentPass : undefined, nextPass);
      setCurrentPass("");
      setNextPass("");
      setRepeatPass("");
      toast.success("Passcode updated. The vault was re-encrypted locally.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not change the passcode.");
    } finally {
      setRotating(false);
    }
  };

  const stamp = (value: number | null | undefined) =>
    value ? new Date(value).toLocaleString() : "—";

  const kind =
    vault?.kind === "passkey"
      ? "Passkey (device biometrics)"
      : biometricsEnabled
        ? "Device biometrics + 6-digit passcode"
        : "6-digit passcode";

  return (
    <div className="animate-rise space-y-4">
      <div>
        <h1 className="text-[20px] font-semibold">Settings</h1>
        <p className="readout mt-1 text-[10px] text-faint">
          Everything here runs on this device. No server ever sees your key.
        </p>
      </div>

      <section className="panel space-y-3 p-4">
        <div className="flex items-center gap-2 text-success">
          <ShieldCheck className="size-4" />
          <p className="text-[13px] font-medium">Key held locally</p>
        </div>
        <dl className="readout space-y-2 text-[11px]">
          <div className="flex justify-between gap-3">
            <dt className="text-faint">PROTECTION</dt>
            <dd className="text-right">{kind}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-faint">ENCRYPTION</dt>
            <dd className="text-right">AES-256-GCM</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-faint">ADDRESS</dt>
            <dd className="text-right">{truncateAddress(address ?? "", 6, 6)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-faint">FINGERPRINT</dt>
            <dd className="text-right text-accent">{addressFingerprint(address ?? "")}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-faint">BACKUP</dt>
            <dd className={`text-right ${vault?.backedUp ? "text-success" : "text-danger"}`}>
              {vault?.backedUp ? "confirmed" : "not confirmed"}
            </dd>
          </div>
        </dl>
      </section>

      <section className="panel space-y-3 p-4">
        <div className="flex items-center gap-2">
          <PenLine className="size-4 text-accent" />
          <p className="text-[13px] font-medium">Signing protections</p>
        </div>
        {[
          {
            key: "holdToSign" as const,
            title: "Hold to confirm",
            copy: "Signing requires a sustained press, so an accidental tap can never write to the ledger.",
          },
          {
            key: "confirmChecksum" as const,
            title: "Checksum confirmation",
            copy: "Show the recipient fingerprint on the review sheet and require it to be acknowledged.",
          },
          {
            key: "biometricSigning" as const,
            title: "Biometric signing",
            copy: "Require Face ID / Touch ID (or Windows Hello) for every signature, not just for unlocking. Needs biometric unlock enrolled below.",
          },
          {
            key: "hideBalances" as const,
            title: "Hide balances by default",
            copy: "Blur amounts until you tap them — useful in public.",
          },
        ].map((row) => (
          <label key={row.key} className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={settings[row.key]}
              onChange={(e) => {
                if (row.key === "biometricSigning" && e.target.checked && !biometricsEnabled) {
                  toast.error("Enrol biometric unlock first, then require it for signing.");
                  return;
                }
                updateSettings({ [row.key]: e.target.checked });
              }}
              className="mt-0.5 size-4 accent-[var(--color-accent)]"
            />
            <span>
              <span className="text-[12px]">{row.title}</span>
              <span className="readout mt-0.5 block text-[10px] leading-relaxed text-faint">
                {row.copy}
              </span>
            </span>
          </label>
        ))}
      </section>

      <section className="panel space-y-3 p-4">
        <div className="flex items-center gap-2">
          <MonitorSmartphone className="size-4 text-accent" />
          <p className="text-[13px] font-medium">Device & session</p>
        </div>
        <dl className="readout space-y-2 text-[11px]">
          <div className="flex justify-between gap-3">
            <dt className="text-faint">DEVICE</dt>
            <dd className="text-right">{session?.device ?? "—"}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-faint">VAULT CREATED</dt>
            <dd className="text-right">{stamp(vault?.createdAt)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-faint">FIRST UNLOCK</dt>
            <dd className="text-right">{stamp(session?.firstUnlockAt)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-faint">THIS SESSION</dt>
            <dd className="text-right">{stamp(session?.lastUnlockAt)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-faint">UNLOCKS</dt>
            <dd className="text-right">{session?.unlockCount ?? 0}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-faint">KEY IN MEMORY</dt>
            <dd className="text-right text-success">unlocked · wiped on lock</dd>
          </div>
        </dl>
        <p className="readout text-[10px] leading-relaxed text-faint">
          Sessions are local to this browser only — there is nothing to revoke remotely. Erase the
          vault below to remove access from this device.
        </p>
      </section>

      <section className="panel space-y-2.5 p-4">
        <div className="flex items-center gap-2">
          <Fingerprint className="size-4 text-accent" />
          <p className="text-[13px] font-medium">Biometric unlock</p>
          <span
            className={`readout ml-auto text-[10px] uppercase tracking-[0.15em] ${
              biometricsEnabled ? "text-success" : "text-faint"
            }`}
          >
            {biometricsEnabled ? "on" : "off"}
          </span>
        </div>
        <p className="readout text-[10px] leading-relaxed text-faint">
          Face ID, Touch ID or Windows Hello releases a key held by this device's secure hardware
          (WebAuthn PRF) and decrypts the vault — no passcode typing. Your 6-digit passcode and
          recovery phrase remain as backups, and the seed never leaves this device.
        </p>
        {biometricsSupported ? (
          <button
            type="button"
            disabled={bioBusy || (vault?.kind === "passkey" && biometricsEnabled)}
            onClick={() => void toggleBiometrics()}
            className={`${biometricsEnabled ? "btn-quiet" : "btn-accent"} h-10 w-full disabled:opacity-40`}
          >
            {bioBusy
              ? "Working…"
              : vault?.kind === "passkey"
                ? "Passkey vault — biometrics required"
                : biometricsEnabled
                  ? "Turn off biometric unlock"
                  : "Enable biometric unlock"}
          </button>
        ) : (
          <p className="readout text-[10px] text-danger">
            This browser does not expose a biometric authenticator. Use your passcode here.
          </p>
        )}
      </section>

      <section className="panel space-y-2.5 p-4">
        <div className="flex items-center gap-2">
          <KeyRound className="size-4 text-accent" />
          <p className="text-[13px] font-medium">
            {vault?.kind === "passphrase" ? "Change vault passcode" : "Set a vault passcode"}
          </p>
        </div>
        <p className="readout text-[10px] leading-relaxed text-faint">
          {vault?.kind === "passphrase"
            ? "The seed is decrypted with your current passcode and immediately re-encrypted under the new one. Your recovery phrase does not change."
            : "This vault is passkey-protected. Setting a 6-digit passcode re-encrypts the seed under PBKDF2 instead — you will unlock with the passcode from then on."}
        </p>
        {vault?.kind === "passphrase" ? (
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={currentPass}
            autoComplete="current-password"
            onChange={(e) => setCurrentPass(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="Current passcode"
            className="field h-11 w-full px-3 text-center tracking-[0.5em]"
          />
        ) : null}
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={nextPass}
          autoComplete="new-password"
          onChange={(e) => setNextPass(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="New 6-digit passcode"
          className="field h-11 w-full px-3 text-center tracking-[0.5em]"
        />
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={repeatPass}
          autoComplete="new-password"
          onChange={(e) => setRepeatPass(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="Repeat new passcode"
          className="field h-11 w-full px-3 text-center tracking-[0.5em]"
        />
        <button
          type="button"
          disabled={
            rotating ||
            nextPass.length !== 6 ||
            repeatPass.length !== 6 ||
            (vault?.kind === "passphrase" && !currentPass)
          }
          onClick={() => void rotate()}
          className="btn-accent h-11 w-full disabled:opacity-40"
        >
          {rotating ? "Re-encrypting…" : "Update passcode"}
        </button>
      </section>

      <section className="panel space-y-2.5 p-4">
        <div className="flex items-center gap-2">
          <Lock className="size-4 text-accent" />
          <p className="text-[13px] font-medium">Auto-lock</p>
        </div>
        <p className="readout text-[10px] leading-relaxed text-faint">
          The decrypted key is wiped from memory after this much inactivity.
        </p>
        <div className="grid grid-cols-4 gap-2">
          {LOCK_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => updateSettings({ autoLockSeconds: o.value })}
              className={`readout h-9 rounded-lg border text-[11px] ${
                settings.autoLockSeconds === o.value
                  ? "border-accent text-accent"
                  : "border-border text-muted hover:bg-elevated"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </section>

      <section className="panel space-y-2.5 p-4">
        <div className="flex items-center gap-2">
          <KeyRound className="size-4 text-accent" />
          <p className="text-[13px] font-medium">Large-transfer guard</p>
        </div>
        <p className="readout text-[10px] leading-relaxed text-faint">
          Transfers above this amount get an extra warning before you can sign.
        </p>
        <div className="flex items-center gap-2">
          <input
            value={String(settings.largeAmountThreshold)}
            onChange={(e) =>
              updateSettings({
                largeAmountThreshold: Number(e.target.value.replace(/[^0-9.]/g, "")) || 0,
              })
            }
            inputMode="decimal"
            className="readout h-11 flex-1 rounded-lg border border-border bg-background px-3 text-[13px] outline-none focus:border-accent"
          />
          <span className="readout text-[11px] text-faint">XRP</span>
        </div>
      </section>

      <section className="panel space-y-2.5 p-4">
        <div className="flex items-center gap-2">
          <Wifi className="size-4 text-accent" />
          <p className="text-[13px] font-medium">Network & privacy</p>
        </div>
        <ul className="readout space-y-1 text-[10px] text-muted">
          {MAINNET_ENDPOINTS.map((e) => (
            <li key={e} className="flex items-center gap-2">
              <span className="size-1.5 rounded-full bg-success" />
              {e}
            </li>
          ))}
        </ul>
        <label className="mt-1 flex items-start gap-3">
          <input
            type="checkbox"
            checked={settings.fiatLookup}
            onChange={(e) => updateSettings({ fiatLookup: e.target.checked })}
            className="mt-0.5 size-4 accent-[var(--color-accent)]"
          />
          <span>
            <span className="text-[12px]">Fiat price lookup</span>
            <span className="readout mt-0.5 block text-[10px] leading-relaxed text-faint">
              Fetches an XRP/USD spot price from a third party. Turn it off to keep every request on
              the ledger.
            </span>
          </span>
        </label>
      </section>

      <section className="panel space-y-2.5 p-4">
        <p className="text-[13px] font-medium">Manage</p>
        <Link
          to="/contacts"
          className="readout flex h-11 items-center justify-center gap-2 rounded-lg border border-border text-[11px] uppercase tracking-[0.15em] text-muted hover:bg-elevated"
        >
          <Users className="size-3" /> Address book
        </Link>
        <Link
          to="/trustlines"
          search={{ q: "" }}
          className="readout flex h-11 items-center justify-center rounded-lg border border-border text-[11px] uppercase tracking-[0.15em] text-muted hover:bg-elevated"
        >
          Trustlines
        </Link>
        <button
          type="button"
          onClick={() => {
            lock();
            toast.success("Wallet locked.");
          }}
          className="readout h-11 w-full rounded-lg border border-border text-[11px] uppercase tracking-[0.15em] text-muted hover:bg-elevated"
        >
          Lock now
        </button>
      </section>

      <section className="panel space-y-2.5 p-4">
        <p className="text-[13px] font-medium">Cobalt service fees</p>
        <p className="readout text-[10px] leading-relaxed text-faint">
          Fees are charged as a separate XRP payment, and only after your transaction has been
          validated on-ledger. Failed or unfilled transactions are never charged. Network fees
          charged by the XRP Ledger itself are separate and go to no one.
        </p>
        <ul className="readout divide-y divide-border/60 rounded-lg border border-border bg-background">
          {feeSchedule.map((row) => (
            <li key={row.label} className="flex justify-between gap-3 px-3 py-2 text-[11px]">
              <span className="text-faint">{row.label}</span>
              <span className={row.value === "Free" ? "text-success" : "text-accent"}>
                {row.value}
              </span>
            </li>
          ))}
        </ul>
        {!FEE_DESTINATION ? (
          <p className="readout text-[10px] leading-relaxed text-muted">
            Fee collection is currently disabled — no collection account is configured, so nothing
            is charged.
          </p>
        ) : null}
      </section>



      <section className="panel space-y-2.5 border-danger/40 p-4">
        <div className="flex items-center gap-2 text-danger">
          <Trash2 className="size-4" />
          <p className="text-[13px] font-medium">Remove wallet from this device</p>
        </div>
        <p className="readout text-[10px] leading-relaxed text-faint">
          This deletes the encrypted vault. Without your recovery seed the funds are unrecoverable.
          Type ERASE to enable.
        </p>
        <input
          value={confirmWipe}
          onChange={(e) => setConfirmWipe(e.target.value.toUpperCase())}
          placeholder="ERASE"
          className="readout h-11 w-full rounded-lg border border-border bg-background px-3 text-[12px] tracking-[0.2em] outline-none placeholder:text-faint focus:border-danger"
        />
        <button
          type="button"
          disabled={confirmWipe !== "ERASE"}
          onClick={() => {
            wipe();
            toast.success("Vault erased from this device.");
            void navigate({ to: "/" });
          }}
          className="readout h-11 w-full rounded-lg bg-danger text-[11px] uppercase tracking-[0.15em] text-background disabled:opacity-40"
        >
          Erase vault
        </button>
      </section>

      <SiteFooter />
    </div>
  );
}
